import { useState } from 'react'
import { regenerateRecoveryCodes, twoFactorConfirm, twoFactorSetup } from '../adminApi'
import { errorMessage } from '../../core/errors'
import type { AdminUser, TwoFactorSetup } from '../../core/types/adminRequests'

export type SetupStep = 'intro' | 'scan' | 'codes' | 'on'

/** De stappen van 2FA instellen. De server maakt de sleutel en controleert de code; hier alleen de flow. */
export function useTwoFactorSetup(user: AdminUser) {
  const [step, setStep] = useState<SetupStep>(user.twoFactor.enabled ? 'on' : 'intro')
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [confirmedUser, setConfirmedUser] = useState<AdminUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (fn: () => Promise<void>, fallback: string) => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(errorMessage(err) ?? fallback)
    } finally {
      setBusy(false)
    }
  }

  const start = () =>
    run(async () => {
      setSetup(await twoFactorSetup())
      setCode('')
      setStep('scan')
    }, 'De QR-code kon niet worden gemaakt. Probeer het zo nog eens.')

  const confirm = () => {
    if (code.length !== 6) return setError('Vul de zes cijfers uit je app in.')
    return run(async () => {
      try {
        const res = await twoFactorConfirm(code)
        setConfirmedUser(res.user)
        setCodes(res.recoveryCodes)
        setStep('codes')
      } catch (err) {
        setCode('')
        throw err
      }
    }, 'Bevestigen lukte niet. Probeer het zo nog eens.')
  }

  const regenerate = () =>
    run(async () => {
      setCodes((await regenerateRecoveryCodes()).recoveryCodes)
      setStep('codes')
    }, 'Nieuwe herstelcodes maken lukte niet.')

  /** Na "Klaar" bij de herstelcodes: terug naar de status. */
  const finish = () => {
    setCodes([])
    setStep('on')
  }

  const back = () => {
    setError(null)
    setStep('intro')
  }

  return {
    step,
    setup,
    code,
    setCode: (v: string) => setCode(v.replace(/\D/g, '').slice(0, 6)),
    codes,
    confirmedUser,
    error,
    busy,
    start,
    confirm,
    regenerate,
    finish,
    back,
  }
}
